/** SUNTRONIX ONE — Inward Reconciliation Wizard.
 *
 *  Opens from an EXC-INWARD-UNEXPECTED-SERIAL exception record.  Two
 *  moves:
 *
 *    move   → pick the correct reverse pickup, add the asset to its
 *             received_asset_ids, and auto-resolve the exception.
 *    reject → return the asset to the customer; auto-resolves the
 *             exception with a rejection note.
 *
 *  Reuses `Dialog` / `Select` from shadcn.  All API traffic goes
 *  through `POST /api/warehouse/reconcile-inward`.
 */
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { AlertTriangle, Package, XCircle, Check } from "lucide-react";

export default function InwardReconciliationWizard({
  exception, open, onOpenChange, onResolved,
}) {
  const [pickups, setPickups] = useState([]);
  const [targetId, setTargetId] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [action, setAction] = useState("move"); // "move" | "reject"
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) return;
    setNotes(""); setTargetId(""); setAction("move"); setQ("");
    // Fetch reverse pickups; caller may filter by q server-side later.
    api.get("/pickups", { params: { limit: 200 } })
      .then(({ data }) => setPickups(data.items || data || []))
      .catch(() => setPickups([]));
  }, [open]);

  const meta = exception?.meta || {};
  const scanned = meta.scanned || exception?.entity_code || "—";

  const filteredPickups = pickups.filter((p) => {
    if (!q) return true;
    const s = q.toLowerCase();
    return (p.pickup_code || "").toLowerCase().includes(s)
      || (p.id || "").toLowerCase().includes(s);
  });

  const submit = async () => {
    if (busy || !exception) return;
    if (action === "move" && !targetId) {
      toast.error("Pick a target pickup, or switch to Reject.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/warehouse/reconcile-inward", {
        exception_id: exception.id,
        action,
        target_pickup_id: action === "move" ? targetId : null,
        notes: notes.trim() || null,
      });
      const resolved = (data.resolved_exceptions || []).length;
      toast.success(action === "move"
        ? `Moved to ${pickups.find((p) => p.id === targetId)?.pickup_code || "target"} · ${resolved} exception${resolved === 1 ? "" : "s"} resolved`
        : "Serial rejected · exception resolved");
      onResolved?.(data);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail?.message
        || e.response?.data?.detail
        || "Reconciliation failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="inward-recon-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-amber-600" strokeWidth={1.6} />
            Reconcile unexpected serial
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 text-[13px]">
          <div className="rounded-md border border-amber-200 bg-amber-50 p-3">
            <div className="text-[10px] uppercase tracking-widest text-amber-800 font-semibold mb-1">
              Unexpected scan
            </div>
            <div className="font-mono text-[13px] text-slate-900">{scanned}</div>
            <div className="text-[11px] text-slate-600 mt-1">
              Original pickup:&nbsp;
              <span className="font-mono">{meta.pickup_code || meta.pickup_id || "—"}</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setAction("move")}
              data-testid="inward-recon-tab-move"
              className={
                "flex-1 rounded-md border px-3 py-2 text-left transition-colors " +
                (action === "move"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
              }>
              <div className="flex items-center gap-2">
                <Package className="h-3.5 w-3.5" strokeWidth={1.6} />
                <span className="font-medium">Move to correct pickup</span>
              </div>
              <div className="text-[11px] mt-0.5 opacity-80">
                Add to that pickup&apos;s received list · auto-resolve
              </div>
            </button>
            <button
              type="button"
              onClick={() => setAction("reject")}
              data-testid="inward-recon-tab-reject"
              className={
                "flex-1 rounded-md border px-3 py-2 text-left transition-colors " +
                (action === "reject"
                  ? "border-slate-900 bg-slate-900 text-white"
                  : "border-slate-200 bg-white text-slate-700 hover:border-slate-400")
              }>
              <div className="flex items-center gap-2">
                <XCircle className="h-3.5 w-3.5" strokeWidth={1.6} />
                <span className="font-medium">Reject &amp; return</span>
              </div>
              <div className="text-[11px] mt-0.5 opacity-80">
                Do not receive · notify customer · auto-resolve
              </div>
            </button>
          </div>

          {action === "move" && (
            <div className="space-y-2">
              <Input
                placeholder="Search pickup code…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                data-testid="inward-recon-pickup-search"
              />
              <Select value={targetId} onValueChange={setTargetId}>
                <SelectTrigger data-testid="inward-recon-pickup-select">
                  <SelectValue placeholder="Target pickup" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {filteredPickups.map((p) => (
                    <SelectItem key={p.id} value={p.id}>
                      <span className="font-mono">{p.pickup_code || p.id}</span>
                      {p.status && <span className="ml-2 text-[10px] text-slate-500">{p.status}</span>}
                    </SelectItem>
                  ))}
                  {filteredPickups.length === 0 && (
                    <div className="px-3 py-2 text-[12px] text-slate-500">
                      No pickups match.
                    </div>
                  )}
                </SelectContent>
              </Select>
            </div>
          )}

          <Textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Note for the audit trail (optional)…"
            rows={2}
            data-testid="inward-recon-notes"
          />
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={busy}
            data-testid="inward-recon-cancel">Cancel</Button>
          <Button onClick={submit} disabled={busy || (action === "move" && !targetId)}
            data-testid="inward-recon-submit">
            {busy ? "Working…" : (
              <span className="inline-flex items-center gap-1">
                <Check className="h-3.5 w-3.5" /> {action === "move" ? "Move & resolve" : "Reject & resolve"}
              </span>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
